import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import { useSelector, useDispatch } from 'react-redux';

import { selectCartItems, selectCartTotals, selectCustomer, clearCart } from '../store/cartSlice';
import { usePosProfile } from './PosProfileContext';
import { createAndSubmitInvoice, buildInvoicePayload } from '../api/invoice';
import { enqueue } from '../offline/outbox';

/**
 * Checkout dialog — payment method selection, amount entry, and submit.
 *
 * - Shows the cart subtotal.
 * - Lists payment methods from the POS Profile.
 * - Allows splitting across methods.
 * - Calculates change for overpayment.
 * - If online: creates + submits POS Invoice.
 * - If offline: queues in outbox for later sync.
 */
export default function CheckoutDialog({ open, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const items = useSelector(selectCartItems);
  const { subtotal } = useSelector(selectCartTotals);
  const customer = useSelector(selectCustomer);
  const { profile, profileData } = usePosProfile();

  // Payment methods from POS Profile
  const paymentMethods = useMemo(() => {
    const methods = profileData?.payments || [];
    return methods.length > 0
      ? methods.map((m) => m.mode_of_payment)
      : ['Cash'];
  }, [profileData]);

  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset payment amounts every time the dialog opens or subtotal changes
  useEffect(() => {
    if (open) {
      const amounts = {};
      paymentMethods.forEach((m, i) => {
        amounts[m] = i === 0 ? subtotal : 0;
      });
      setPaymentAmounts(amounts);
      setError(null);
      setLoading(false);
    }
  }, [open, subtotal, paymentMethods]);

  const totalPaid = Object.values(paymentAmounts).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const change = Math.max(0, totalPaid - subtotal);
  const remaining = Math.max(0, subtotal - totalPaid);

  const handlePaymentChange = (method, value) => {
    setPaymentAmounts((prev) => ({ ...prev, [method]: value }));
  };

  // Quick-fill: click a method chip to set it to the remaining amount
  const handleQuickFill = (method) => {
    if (remaining > 0) {
      setPaymentAmounts((prev) => ({
        ...prev,
        [method]: ((parseFloat(prev[method]) || 0) + remaining).toFixed(2),
      }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    const payments = Object.entries(paymentAmounts)
      .filter(([, amt]) => parseFloat(amt) > 0)
      .map(([mode_of_payment, amount]) => ({
        mode_of_payment,
        amount: parseFloat(amount),
      }));

    if (payments.length === 0) {
      setError('At least one payment method is required.');
      setLoading(false);
      return;
    }

    const isOnline = navigator.onLine;

    try {
      if (isOnline) {
        // Online: create + submit POS Invoice directly
        const invoice = await createAndSubmitInvoice({
          profile,
          profileData,
          customer: customer?.name,
          items,
          payments,
        });

        dispatch(clearCart());
        onSuccess({
          mode: 'online',
          invoice,
          items,
          payments,
          subtotal,
          change,
        });
      } else {
        // Offline: queue in outbox for later sync
        const payload = buildInvoicePayload({
          profile,
          profileData,
          customer: customer?.name,
          items,
          payments,
        });
        const queued = await enqueue('pos_invoice', payload);

        dispatch(clearCart());
        onSuccess({
          mode: 'offline',
          queuedId: queued.id,
          items,
          payments,
          subtotal,
          change,
        });
      }
    } catch (err) {
      const msg = err?.response?.data?.exc_type
        ? `${err.response.data.exc_type}: ${err.response.data._server_messages || err.message}`
        : err.message || 'Checkout failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PaymentIcon />
          <span>Checkout</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!navigator.onLine && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are offline. The sale will be queued and synced when you reconnect.
          </Alert>
        )}

        {/* Order summary */}
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {items.length} item{items.length !== 1 ? 's' : ''} in cart
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            Total: {subtotal.toFixed(2)}
          </Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Payment methods */}
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Payment Methods
        </Typography>

        <Stack spacing={2}>
          {paymentMethods.map((method) => (
            <Stack key={method} direction="row" alignItems="center" spacing={1.5}>
              <Chip
                label={method}
                variant={parseFloat(paymentAmounts[method]) > 0 ? 'filled' : 'outlined'}
                color={parseFloat(paymentAmounts[method]) > 0 ? 'primary' : 'default'}
                onClick={() => handleQuickFill(method)}
                sx={{ minWidth: 80 }}
              />
              <TextField
                size="small"
                type="number"
                value={paymentAmounts[method]}
                onChange={(e) => handlePaymentChange(method, e.target.value)}
                inputProps={{ min: 0, step: '0.01' }}
                sx={{ width: 140 }}
              />
            </Stack>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Totals */}
        <Stack spacing={0.5}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Total Paid</Typography>
            <Typography variant="body2" fontWeight={600}>{totalPaid.toFixed(2)}</Typography>
          </Stack>
          {remaining > 0 && (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="error">Remaining</Typography>
              <Typography variant="body2" color="error" fontWeight={600}>
                {remaining.toFixed(2)}
              </Typography>
            </Stack>
          )}
          {change > 0 && (
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="success.main">Change</Typography>
              <Typography variant="body2" color="success.main" fontWeight={700}>
                {change.toFixed(2)}
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={loading || remaining > 0 || items.length === 0}
        >
          {loading ? (
            <CircularProgress size={22} color="inherit" />
          ) : navigator.onLine ? (
            'Complete Sale'
          ) : (
            'Save Offline'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
